import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitFormData } from '../../../pages/api';

const Form_8 = () => {
    const navigate = useNavigate();
    const laminators = ['Laminator 1', 'Laminator 2', 'Laminator 3'];

    // State management
    const [lines, setLines] = useState([]);
    const [selectedLine, setSelectedLine] = useState('');
    const [formData, setFormData] = useState(() => {
        const savedData = sessionStorage.getItem('LaminationMachineF8formData');
        return savedData ? JSON.parse(savedData) : {};
    });
    const [isLoading, setIsLoading] = useState(false);
    const [metadata, setMetadata] = useState(null);

    // Fetch lines and metadata from sessionStorage
    useEffect(() => {
        const metadataString = sessionStorage.getItem('metadata');
        if (metadataString) {
            try {
                const parsedMetadata = JSON.parse(metadataString);
                setMetadata(parsedMetadata);
                const fetchedLines = parsedMetadata.lines.map(item => String(item.Lines)); // Convert to string
                setLines(fetchedLines);
                setSelectedLine(fetchedLines[0] || '');
            } catch (error) {
                console.error('Error parsing metadata:', error);
            }
        }
    }, []);

    // Prefill date and shift from sessionStorage
    useEffect(() => {
        const inspectionDate = sessionStorage.getItem('inspectionDate')?.split('-').reverse().join('-');
        const inspectionShift = sessionStorage.getItem('inspectionShift');
        if (inspectionDate && inspectionShift && !formData.date) {
            setFormData(prev => ({
                ...prev,
                date: inspectionDate,
                shift: inspectionShift
            }));
        }
    }, []);

    // Prefill data from inspectionData for all lines on initial render
    useEffect(() => {
        const prefillAllLines = () => {
            const inspectionData = sessionStorage.getItem('inspectionData');
            if (!inspectionData || lines.length === 0) return;
    
            try {
                const parsedInspectionData = JSON.parse(inspectionData);
                const laminationMachineData = parsedInspectionData.LaminationMachine;
    
                lines.forEach(line => {
                    const isLineDataEmpty = !formData[line] ||
                        Object.values(formData[line]).every(laminator =>
                            Object.values(laminator).every(value => value === '' || value === null || value === undefined)
                        );
    
                    if (isLineDataEmpty && laminationMachineData[line] && Object.keys(laminationMachineData[line]).length > 0) {
                        setFormData(prev => ({
                            ...prev,
                            [String(line)]: { // Ensure key is a string
                                'Laminator 1': { ...laminationMachineData[line]['Laminator 1'] },
                                'Laminator 2': { ...laminationMachineData[line]['Laminator 2'] },
                                'Laminator 3': { ...laminationMachineData[line]['Laminator 3'] }
                            }
                        }));
                    }
                });
            } catch (error) {
                console.error('Error parsing inspectionData:', error);
            }
        };
    
        if (lines.length > 0) {
            prefillAllLines();
        }
    }, [lines]);
    
    // Handle line selection
    const handleLineChange = (e) => {
        setSelectedLine(String(e.target.value));
    };

    // Handle input changes
    const handleChange = (laminator, e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [selectedLine]: {
                ...prev[selectedLine],
                [laminator]: {
                    ...prev[selectedLine]?.[laminator],
                    [name]: value
                }
            }
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        setIsLoading(true);

        try {
            const payload = {
                projectId: localStorage.getItem('selectedProject'),
                inspectionSupplier: sessionStorage.getItem('inspectionSupplier'),
                inspectionDate: sessionStorage.getItem('inspectionDate'),
                inspectionShift: formData.shift,
                inspectorName: sessionStorage.getItem('username'),
                status: 'completed',
                'LaminationMachine': { 
                    ...lines.reduce((acc, line) => ({
                    ...acc,
                    [line]: formData[line] || {}
                }), {})
                }
            };

            await submitFormData(payload);
            alert('All forms submitted successfully!');
        } catch (error) {
            console.error('Error submitting form:', error);
            alert(`Failed to submit form: ${error.message || 'Unknown error'}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Save to sessionStorage
    useEffect(() => {
        sessionStorage.setItem('LaminationMachineF8formData', JSON.stringify(formData));
    }, [formData]);

    // Function to get chamber values for a specific line and laminator
    const getChamberValues = (line, laminator) => {
        if (!metadata || !metadata.laminationMachineParameterVerif) {
            return { chamber1: '', chamber2: '' };
        }
    
        const params = metadata.laminationMachineParameterVerif.find(
            item => String(item.Line) === String(line) && item.Laminator === laminator
        );
    
        return {
            chamber1: params ? params.Chamber1 : '',
            chamber2: params ? params.Chamber2 : ''
        };
    };

    return (
        <div className="min-h-screen bg-gray-100 p-2">
            <div className="w-full mx-auto">
                {/* Header Section */}
                <div className="bg-white rounded-lg shadow-lg p-4 Graphite Neutral 600 mb-6">
                    <h2 className="text-2xl font-bold text-center text-gray-800">
                        Lamination Machine Parameters Verification
                    </h2>
                    <p className="text-center text-sm text-gray-600 mt-1">
                        Frequency: Shift Wise
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        <div>
                            <p className="text-sm font-medium">
                                Date: {formData.date}
                            </p>
                            <p className="text-sm font-medium">Shift: {formData.shift}</p>
                        </div>
                        <div className="justify-center">
                            <label className="block text-md font-semibold text-gray-700">Select Line:</label>
                            <select
                                value={selectedLine}
                                onChange={handleLineChange}
                                className="w-full max-w-xs p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
                                disabled={lines.length === 0}
                            >
                                {lines.length === 0 ? (
                                    <option value="">No lines available</option>
                                ) : (
                                    lines.map(line => (
                                        <option key={line} value={line}>{line}</option>
                                    ))
                                )}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Forms Section */}
                {lines.length > 0 && (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {laminators.map(laminator => {
                            // Get chamber values for the current line and laminator
                            const { chamber1, chamber2 } = getChamberValues(selectedLine, laminator);

                            // Define fields dynamically for each laminator
                            const fields = [
                                { label: `Set Temp.(${chamber1})`, name: 'chamber1Temp',type: 'number' },
                                { label: `Set Temp.(${chamber2})`, name: 'chamber2Temp',type: 'number' },
                                { label: 'Vacuum Time B1 (s)', name: 'vacuumTimeB1' },
                                { label: 'Vacuum Time B2 (s)', name: 'vacuumTimeB2' },
                                { label: 'Total Cycle Time', name: 'totalCycleTime' },
                                { label: 'Cooling Time', name: 'coolingTime' },
                                { label: 'Remarks', name: 'remarks', type: 'textarea' },
                            ];

                            return (
                                <div
                                    key={laminator}
                                    className="bg-white rounded-lg shadow-lg p-6"
                                >
                                    <h3 className="text-lg font-semibold text-gray-700 mb-4">{laminator}</h3>
                                    <div className="grid grid-cols-4 md:grid-cols-4 gap-4">
                                        {fields.map(({ label, name, type }) => (
                                            <div key={name}>
                                                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={`${laminator}-${name}`}>
                                                    {label}
                                                </label>
                                                {type === 'textarea' ? (
                                                    <textarea
                                                        id={`${laminator}-${name}`}
                                                        name={name}
                                                        value={formData[selectedLine]?.[laminator]?.[name] || ''}
                                                        onChange={(e) => handleChange(laminator, e)}
                                                        className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-gray-500"
                                                    />
                                                ) : (
                                                    <input
                                                        id={`${laminator}-${name}`}
                                                        type={type} 
                                                        name={name}
                                                        value={formData[selectedLine]?.[laminator]?.[name] || ''}
                                                        onChange={(e) => handleChange(laminator, e)}
                                                        className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 border-gray-300 focus:ring-gray-500"
                                                    />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                        <div className="flex justify-between mt-6">
                            <button
                                type="button"
                                onClick={() => navigate('/inspections/section2/form7')}
                                className="bg-gray-300 text-gray-700 font-medium py-2 px-6 rounded hover:bg-gray-300 focus:outline-none transition-colors"
                            >
                                Previous
                            </button>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="bg-[#424752] text-white font-medium py-2 px-6 rounded hover:bg-gray-700 focus:outline-none disabled:bg-gray-400 transition-colors"
                            >
                                {isLoading ? 'Submitting...' : 'Submit'}
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate('/inspections/section2/form9')}
                                className="bg-gray-300 text-gray-700 font-medium py-2 px-6 rounded hover:bg-gray-300 focus:outline-none transition-colors"
                            >
                                Next
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default Form_8;